import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DpCardMenuPlacement, DpCardTitle, DpChartColor, DpFilterCard, DpLineChart, DpLineChartOptions, DpLineXAxisParameters } from '@docpath/webcomponents';
import { LineSeriesData, LineSeriesDataParameters } from '@docpath/webcomponents/shared/chart/models/line-chart.model';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CloudService } from 'src/app/shared/services/cloud.service';

import { Constants } from '../../../../constants/constants';
import { SearchKeyType, StatisticsData, StatisticsSearch } from '../../models/statistics';
import { StatisticsService } from '../../services/statistics.service';
import { StatisticsHttpService } from '../../services/statistics-http.service';

@Component({
  selector: 'app-statistics-chart',
  templateUrl: './statistics-chart.component.html'
})
export class StatisticsChartComponent implements OnInit, OnDestroy {

  private readonly i18nPrefixMonths = 'statistics.chart.months';

  leftLineChart?: DpLineChart;
  rightLineChart?: DpLineChart;
  showLoadingLeftChart = false;
  showLoadingRightChart = false;

  @Input() dgeId: number = 0;

  leftFilterCard: DpFilterCard;
  rightFilterCard: DpFilterCard;

  statisticsRefreshChangedSubscription?: Subscription;

  generatedDocuments: StatisticsData[] = [];
  averageWaitTime: StatisticsData[] = [];

  constructor(
    private readonly statisticsHttpService: StatisticsHttpService,
    private readonly statisticsService: StatisticsService,
    private readonly translateService: TranslateService,
        private readonly cloudService: CloudService
  ) {

    this.leftFilterCard = new DpFilterCard({
      title: new DpCardTitle({
        label: this.cloudService.isModeCloud() ? 'statistics.chart.generated-transactions.label' : 'statistics.chart.generated-documents.label',
        tooltip: this.cloudService.isModeCloud() ? 'statistics.chart.generated-transactions.tooltip' : 'hostatisticsme.chart.generated-documents.tooltip'
      }),
      bodyHeight: '350px'
    });
  
    this.rightFilterCard = new DpFilterCard({
      title: new DpCardTitle({
        label: 'statistics.chart.average-wait-time.label',
        tooltip: 'statistics.chart.average-wait-time.tooltip'
      }),
      bodyHeight: '350px',
      menuPlacement: DpCardMenuPlacement.Left
    });

  }

  ngOnInit(): void {
    this.statisticsService.statisticsSelectsChanged.subscribe(statisticsFilter => {
      this.loadLeftChartValues(statisticsFilter.dgeId, statisticsFilter.numberHours, statisticsFilter.customNumberHoursFrom, statisticsFilter.customNumberHoursTo, statisticsFilter.lastJobId);
      this.loadRightChartValues(statisticsFilter.dgeId, statisticsFilter.numberHours, statisticsFilter.customNumberHoursFrom, statisticsFilter.customNumberHoursTo, statisticsFilter.lastJobId);
    });
    if (!this.statisticsRefreshChangedSubscription) {
      this.statisticsRefreshChangedSubscription = this.statisticsService.statisticsRefreshChanged.subscribe(statisticsFilter => {
        this.loadLeftChartValues(statisticsFilter.dgeId, statisticsFilter.numberHours, '', '', 0);
        this.loadRightChartValues(statisticsFilter.dgeId, statisticsFilter.numberHours, '', '', 0);
      });
    }
    this.loadLeftChartValues(this.dgeId ?? Constants.DGE_ALL_VALUES, Constants.NUMBER_OF_HOURS_24, '', '', 0);
    this.loadRightChartValues(this.dgeId ?? Constants.DGE_ALL_VALUES, Constants.NUMBER_OF_HOURS_24, '', '', 0);
  }

  ngOnDestroy(): void {
      if (this.statisticsRefreshChangedSubscription) {
          this.statisticsRefreshChangedSubscription.unsubscribe();
      }
  }

  loadRightChartValues(dgeId: number, numberHours: number, customNumberHoursFrom: string, customNumberHoursTo: string, lastJobId: number): void {
    const search = this.toSearch(SearchKeyType.AverageWaitTime, dgeId, numberHours, customNumberHoursFrom, customNumberHoursTo, lastJobId);

    if (!this.leftLineChart) {
      this.showLoadingRightChart = true;
    }
    this.statisticsHttpService.getStatistics(search).subscribe(result => {
      this.averageWaitTime = result.statisticsData;
      this.leftLineChart = this.initLineChart(this.averageWaitTime, result.timeUnit, DpChartColor.Green, DpChartColor.Green, numberHours);
      this.showLoadingRightChart = false;
    });
  }

  loadLeftChartValues(dgeId: number, numberHours: number, customNumberHoursFrom: string, customNumberHoursTo: string, lastJobId: number): void {
    const search = this.toSearch(
      this.cloudService.isModeCloud() ? SearchKeyType.GeneratedTransactions : SearchKeyType.GeneratedDocuments,
      dgeId,
      numberHours,
      customNumberHoursFrom,
      customNumberHoursTo,
      lastJobId);

    if (!this.rightLineChart) {
      this.showLoadingLeftChart = true;
    }
    this.statisticsHttpService.getStatistics(search).subscribe(result => {
      this.generatedDocuments = result.statisticsData;
      this.rightLineChart = this.initLineChart(this.generatedDocuments, result.timeUnit, DpChartColor.Blue, DpChartColor.Blue, numberHours);
      this.showLoadingLeftChart = false;
    });
  }

  // eslint-disable-next-line max-params
  private toSearch(key: SearchKeyType, dgeId: number, hours: number, customNumberHoursFrom: string, customNumberHoursTo: string, lastJobId: number): StatisticsSearch {
    return new StatisticsSearch(key, dgeId, hours, customNumberHoursFrom, customNumberHoursTo, lastJobId);
  }

  private initLineChart(statisticsData: StatisticsData[], timeUnit: string, lineColor: string, itemColor: string, numberHours: number): DpLineChart {
    const yAxisParameters = Math.max(...statisticsData.map(result => result.yAxis)) < 4 ? { interval: 1 } : undefined;

    const statistics: LineSeriesData[] = statisticsData.map(result => ({
      name: result.xAxis,
      value: [result.xAxis, result.yAxis]
    }));

    const xAxisParameters: DpLineXAxisParameters = { axisLabelFormatter: this.getAxisLabelFormatter(timeUnit), minInterval: this.getXAxisMinInterval(statisticsData.length), splitNumber: this.getSplitNumber(statistics, timeUnit) };

    const seriesData: LineSeriesDataParameters[] = [
      { name: '', value: statistics, lineStyle: { width: 3, color: lineColor }, itemStyle: { color: itemColor }, symbolSize: 6 }
    ];

    const lastValue = seriesData[0].value[seriesData[0].value.length - 1].name;

    const lineChartOptions = new DpLineChartOptions({
      xAxisParameters,
      yAxisParameters,
      seriesData,
      showLegend: false,
      tooltipFormatter: (parameters: unknown) => (numberHours === 0 ? this.getCustomTooltipFormatter(parameters, statisticsData, timeUnit) : this.getTooltipFormatter(parameters, lastValue))
    });

    return new DpLineChart({ options: lineChartOptions });
  }

  private getAxisLabelFormatter(timeUnit: string): (value: number, index: number) => string {
    return (value: number) => {
      const date = new Date(value);
      const monthKey = this.getMonthKey(date.getMonth());
      const monthName = this.translateService.instant(`${this.i18nPrefixMonths}.${monthKey}`);

      switch (timeUnit) {
        case Constants.DAYS:
          return `${date.getDate()} ${monthName}`;
        case Constants.MONTHS:
          return monthName;
        case Constants.YEARS:
          return `${date.getFullYear()}`;
        case Constants.HOURS:
        default:
          return `${date.getHours()}H`;
      }
    };
  }

  private getMonthKey(monthIndex: number): string {
    const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
    return monthKeys[monthIndex];
  }
  private getSplitNumber(data: { name: string, value: [string, number]; }[], timeUnit: string): number {
    const dates = data.map(d => new Date(d.value[0]).getTime());
    const minDate = Math.min(...dates);
    const maxDate = Math.max(...dates);
    const dateRange = maxDate - minDate;

    let splitNumber: number;

    switch (timeUnit) {
      case Constants.DAYS:
        splitNumber = Math.ceil(dateRange / (24 * 3600 * 1000));
        splitNumber = Math.min(splitNumber, 15);
        break;
      case Constants.MONTHS:
        splitNumber = Math.ceil(dateRange / (30 * 24 * 3600 * 1000));
        break;
      case Constants.YEARS:
        splitNumber = Math.ceil(dateRange / (365 * 24 * 3600 * 1000));
        splitNumber = Math.min(splitNumber, 15);
        break;
      case Constants.HOURS:
      default:
        splitNumber = Math.ceil(dateRange / (3600 * 1000));
    }

    return splitNumber;
  }


  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getTooltipFormatter(parameters: any, lastValue: string): string {
    const currentValue = parameters.value[0];
    const date = new Date(parameters.value[0]);
    const hours = date.getHours();
    const minutes = date.getMinutes();

    let formattedTime = '';

    if (currentValue === lastValue) {
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      formattedTime = `${hours}:${formattedMinutes}`;
    } else {
      formattedTime = `${(hours < 10 ? '0' : '') + hours}H`;
    }

    const value = parameters.value[1];

    return `${formattedTime}, ${value}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getCustomTooltipFormatter(parameters: any, statisticsData: StatisticsData[], timeUnit: string): string {
    const currentValue = parameters.value[0];
    const date = new Date(currentValue);
    const { hours, minutes } = this.getTooltipDate(currentValue);
    const { day, monthName, year } = this.getTooltipDayMonthYear(date);

    const isFirstValue = currentValue === statisticsData[0].xAxis;
    const isLastValue = currentValue === statisticsData[statisticsData.length - 1].xAxis;

    let formattedTime = '';

    switch (timeUnit) {
      case Constants.DAYS:
        formattedTime = `${day} ${monthName}`;
        if (isFirstValue || isLastValue) {
          formattedTime += ` ${this.formatHours(hours)}:${this.formatMinutes(minutes)}`;
        }
        break;

      case Constants.MONTHS:
        formattedTime = monthName;
        if (isFirstValue || isLastValue) {
          formattedTime = `${day} ${monthName} ${this.formatHours(hours)}:${this.formatMinutes(minutes)}`;
        }
        break;

      case Constants.YEARS:
        formattedTime = `${year}`;
        if (isFirstValue || isLastValue) {
          formattedTime = `${day} ${monthName} ${year} ${this.formatHours(hours)}:${this.formatMinutes(minutes)}`;
        }
        break;

      case Constants.HOURS:
      default:
        if (statisticsData.length === 1) {
          const tooltipDate = this.getTooltipDate(statisticsData[0].tooltipDate);
          const xAxisDate = this.getTooltipDate(statisticsData[0].xAxis);

          formattedTime = `${this.formatHours(tooltipDate.hours)}:${this.formatMinutes(tooltipDate.minutes)} - ${this.formatHours(xAxisDate.hours)}:${this.formatMinutes(xAxisDate.minutes)}`;
        } else if (isFirstValue) {
          const firstTooltipDate = this.getTooltipDate(statisticsData[0].tooltipDate);
          formattedTime = `${this.formatHours(firstTooltipDate.hours)}:${this.formatMinutes(firstTooltipDate.minutes)} - ${this.formatHours(hours)}H`;
        } else if (isLastValue) {
          formattedTime = `${this.formatHours(hours)}H - ${this.formatHours(hours)}:${this.formatMinutes(minutes)}`;
        } else {
          formattedTime = `${this.formatHours(hours)}H`;
        }
        break;
    }

    const value = parameters.value[1];
    return `${formattedTime}, ${value}`;
  }

  private getTooltipDate(dateString: string): { hours: number, minutes: number; } {
    const date = new Date(dateString);
    return { hours: date.getHours(), minutes: date.getMinutes() };
  }

  private getTooltipDayMonthYear(date: Date): { day: number, monthName: string, year: number; } {
    const day = date.getDate();
    const monthName = this.translateService.instant(`${this.i18nPrefixMonths}.${this.getMonthKey(date.getMonth())}`);
    const year = date.getFullYear();
    return { day, monthName, year };
  }

  private formatMinutes(minutes: number): string {
    return minutes < 10 ? `0${minutes}` : `${minutes}`;
  }

  private formatHours(hours: number): string {
    return hours < 10 ? `0${hours}` : `${hours}`;
  }

  private getXAxisMinInterval(numberHours: number): number {
    let xAxisMinInterval: number;

    if (numberHours > 13) {
      xAxisMinInterval = 8640000;
    } else if (numberHours <= 13 && numberHours > 7) {
      xAxisMinInterval = 4320000;
    } else if (numberHours <= 7 && numberHours > 2) {
      xAxisMinInterval = 2160000;
    } else {
      xAxisMinInterval = 3600000;
    }

    return xAxisMinInterval;
  }

}
